import React, { useState, createRef, useEffect, useCallback } from 'react'
import SyncLoader from 'react-spinners/SyncLoader'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import addParametersToUrl from '../helpers/addParametersToUrl'

const FormInput = ({ name, placeholder, type, children, onChange }) => (
  <div className="w-full mb-8">
    <input
      className="w-full border-0 border-b border-gray-300 hover:outline-none focus:outline-none focus:border-gray-500 transition-all font-light"
      placeholder={placeholder}
      name={name}
      type={type || 'text'}
      onChange={onChange}
    />
    {children}
  </div>
)

const noErrors = {
  'your-name': null,
  'your-email': null,
  'how-did-you-hear-about-us': null,
  'phone-number': null,
  recaptcha: null,
}

const QuoteFormRecaptcha = () => {
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [formErrors, setFormErrors] = useState(noErrors)
  const [recaptchaToken, setRecaptchaToken] = useState()
  const { executeRecaptcha } = useGoogleReCaptcha()

  const formRef = createRef()

  const cookie =
    typeof window !== 'undefined' ? localStorage.getItem('gclid') : null

  // Create an event handler so you can call the verification on button click event or form submit
  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available')
      return
    }

    const token = await executeRecaptcha('quoteform')
    setRecaptchaToken(token)
  }, [executeRecaptcha])

  useEffect(() => {
    handleReCaptchaVerify()
  }, [handleReCaptchaVerify])

  const handleSubmit = async event => {
    event.preventDefault()
    setLoading(true)
    setFormErrors(noErrors)

    try {
      const formElement = formRef.current
      const body = new FormData(formElement)
      body.append('recaptchaToken', recaptchaToken)
      body.set('_wpcf7_unit_tag', '1');

      const quoteFormResponse = await fetch(`/api/quote-form`, {
        method: 'POST',
        body,
      })
      const quoteFormJSON = await quoteFormResponse.json()
      handleReCaptchaVerify()

      if (quoteFormJSON.status === 'validation_failed') {
        const currentErrorState = formErrors

        quoteFormJSON.invalid_fields.forEach(error => {
          const errorKey = error.error_id.replace('-ve-', '')
          currentErrorState[errorKey] = error.message
        })

        setFormErrors(currentErrorState)
        setLoading(false)
        return
      }

      addParametersToUrl('quoteForm')
      setSubmitted(true)
      setLoading(false)
    } catch {
      e => {
        setFormErrors({ recaptcha: e })
        setLoading(false)
        return
      }
    }
  }

  return (
    <div className="relative z-10" id="quoteForm">
      {loading && (
        <div className="absolute inset-0 bg-white bg-opacity-60 flex items-center justify-center">
          <SyncLoader color="#83E8AB" size={10} />
        </div>
      )}
      {submitted ? (
        <div className="w-full h-full flex flex-col items-center justify-center p-10 bg-white">
          <h3>Submitted</h3>
          <p>Thank you for your response</p>
        </div>
      ) : (
        <form
          className="form p-4 md:p-10 bg-white"
          name="quote-form"
          ref={formRef}
          onSubmit={handleSubmit}
        >
          <input type="hidden" name="form_id" value="1" />
          <input
            type="hidden"
            id="gclid_field"
            name="gclid_field"
            value={cookie}
          />

          <FormInput
            name="your-name"
            placeholder="Full Name"
            onChange={() => {
              setFormErrors({ ...formErrors, 'your-name': null })
            }}
          >
            {formErrors['your-name'] && (
              <small className="text-red-500 font-thin">
                {formErrors['your-name']}
              </small>
            )}
          </FormInput>

          <FormInput
            name="your-email"
            placeholder="Email"
            type="email"
            onChange={() => {
              setFormErrors({ ...formErrors, 'your-email': null })
            }}
          >
            {formErrors['your-email'] && (
              <small className="text-red-500 font-thin">
                {formErrors['your-email']}
              </small>
            )}
          </FormInput>

          <div className="md:flex gap-x-10">
            <FormInput
              name="phone-number"
              placeholder="Phone Number"
              type="tel"
            >
              {formErrors['phone-number'] && (
                <small className="text-red-500 font-thin">
                  {formErrors['phone-number']}
                </small>
              )}
            </FormInput>

            <div className="w-full mb-8">
              <select
                name="how-did-you-hear-about-us"
                onChange={() => {
                  setFormErrors({
                    ...formErrors,
                    'how-did-you-hear-about-us': null,
                  })
                }}
                className="bg-transparent w-full border-0 border-b border-gray-300 hover:outline-none focus:outline-none focus:border-gray-500 transition-all font-light"
              >
                <option value="">How did you hear about us</option>
                <option value="11">Referred by a Friend</option>
                <option value="5">1310 The Ticket</option>
                <option value="75">106.1 KISS FM</option>
                <option value="38">105.3 The Fan</option>
                <option value="76">TV/Streaming</option>
                <option value="20">Google</option>
                <option value="51">Facebook</option>
                <option value="2025">Fox4 News</option>
                <option value="2004">KILT John Lopez</option>
                <option value="2002">Email</option>
                <option value="82">Instagram</option>
                <option value="83">YouTube</option>
                <option value="10">Dallas Morning News</option>
                <option value="2029">TCU Football</option>
                <option value="2024">96.3 KSCS - Hawkeye</option>
                <option value="50">97.1 – The Eagle</option>
                <option value="40">820 WBAP- Brian Estridge</option>
                <option value="52">KTRH - Jimmy Barrett</option>
                <option value="2009">KRBE – Producer Eric</option>
              </select>
              {formErrors['how-did-you-hear-about-us'] && (
                <small className="text-red-500 font-thin">
                  {formErrors['how-did-you-hear-about-us']}
                </small>
              )}
            </div>
          </div>
          <div className="md:flex gap-x-10">
            <FormInput name="wanted-car" placeholder="What car do you want?" />
            <FormInput name="current-car" placeholder="What car do you have?" />
          </div>
          <div className="gap-x-10">
            {formErrors.recaptcha && (
              <small className="text-red-500 font-thin mb-4">
                {formErrors.recaptcha}
              </small>
            )}
          </div>
          <button
            type="submit"
            className="block mx-auto uppercase bg-brand-green rounded-full px-8 py-4 text-sm hover:opacity-90 transition-all"
          >
            Send
          </button>
          <div className="mt-8 font-thin text-sm text-gray-500">
            By clicking 'Send', you expressly consent to receive phone calls,
            text messages and/or emails from D&M Leasing.
          </div>
          <div className="mt-8 font-thin text-sm text-gray-500">
            This site is protected by reCAPTCHA and the Google{' '}
            <a className="underline" href="https://policies.google.com/privacy">
              Privacy Policy
            </a>{' '}
            and{' '}
            <a className="underline" href="https://policies.google.com/terms">
              Terms of Service
            </a>{' '}
            apply.
          </div>
        </form>
      )}
    </div>
  )
}

export default QuoteFormRecaptcha
